import { state, currentUser } from '../state';
import { apiCall } from '../api';
import { showToast, formatFileLabel } from '../utils';
import { defaultSystemDict, setSystemDict, getDisplayColumnName, systemDict, resetMassColumnSettingsCache, resetSingleColumnSettingsCache } from './common';
import { classificationPicker, ClassificationPickerValue } from '../components/classification-picker';

function getEl<T extends HTMLElement>(id: string): T | null { return document.getElementById(id) as T | null; }
function getInput(id: string): HTMLInputElement | null { return getEl<HTMLInputElement>(id); }
function getSelect(id: string): HTMLSelectElement | null { return getEl<HTMLSelectElement>(id); }
function getButton(id: string): HTMLButtonElement | null { return getEl<HTMLButtonElement>(id); }
const Tabulator = (window as any).Tabulator;

// -------------------- АДМИНИСТРИРОВАНИЕ --------------------
// Известные колонки для редакторов (заполняются при открытии страницы админа)
let adminKnownColumns: { master_parser: string[]; sveta_server: string[] } = {
    master_parser: [],
    sveta_server: Object.keys(defaultSystemDict)
};
let defaultMasterParserLabels: Record<string, string> = {};

async function loadDefaultMasterParserLabels() {
    const data = await apiCall('/api/parser/rename_map', 'GET');
    if (data && data.rename_map) {
        defaultMasterParserLabels = data.rename_map as Record<string, string>;
    }
}

export async function fetchKnownColumns() {
    // sveta_server: ключи из defaultSystemDict + колонки из активных справочников
    const rawCols = await apiCall('/api/reference/raw_columns', 'GET');
    const svSet = new Set([...Object.keys(defaultSystemDict), ...(rawCols?.columns || [])]);
    adminKnownColumns.sveta_server = Array.from(svSet).sort();

    // master_parser: уникальные ключи атрибутов из всех групп + дефолтные labels парсера
    const attrs = await apiCall('/api/attributes/all', 'GET');
    const mpSet = new Set<string>();
    if (attrs) {
        Object.values(attrs).forEach((groupAttrs: any) => {
            (groupAttrs || []).forEach((a: any) => mpSet.add(a.key));
        });
    }
    await loadDefaultMasterParserLabels();
    Object.keys(defaultMasterParserLabels).forEach(k => mpSet.add(k));
    adminKnownColumns.master_parser = Array.from(mpSet).sort();
}

export async function loadColLabels() {
    // Пробуем загрузить с сервера
    const data = await apiCall('/api/admin/column_labels', 'GET');
    if (data && data.labels) {
        state.colLabels = data.labels;
    } else {
        // Fallback на localStorage
        try {
            const stored = localStorage.getItem('colLabels');
            if (stored) state.colLabels = JSON.parse(stored);
        } catch (e) {
            console.error('Ошибка загрузки colLabels', e);
        }
    }
    setSystemDict({ ...defaultSystemDict, ...(state.colLabels.sveta_server || {}) });
}

export async function saveColLabels() {
    const res = await apiCall('/api/admin/column_labels', 'POST', { labels: state.colLabels });
    if (res && res.success) {
        setSystemDict({ ...defaultSystemDict, ...(state.colLabels.sveta_server || {}) });
        showToast('Переименования сохранены', 'success');
    }
}

(window as any).clearAdminLog = function () {
    const body = document.getElementById('admin-log-body');
    if (body) body.innerHTML = '';
};

export function getDefaultLabel(mapKey: string, key: string): string {
    if (mapKey === 'sveta_server' && defaultSystemDict[key]) {
        return defaultSystemDict[key];
    }
    if (mapKey === 'master_parser' && defaultMasterParserLabels[key]) {
        return defaultMasterParserLabels[key];
    }
    return '';
}

export function renderRenameEditor(containerId: string, mapKey: string) {
    const container = getEl<HTMLElement>(containerId);
    if (!container) return;
    const known = adminKnownColumns[mapKey as keyof typeof adminKnownColumns] || [];
    const map: Record<string, string> = (state.colLabels as any)[mapKey] || {};

    // Все колонки: известные + дополнительные из map
    const allKeys = new Set([...known, ...Object.keys(map)]);

    let html = '<div style="display:flex; flex-direction:column; gap:6px; max-height:400px; overflow-y:auto;">';
    Array.from(allKeys).sort().forEach(key => {
        const hasCustom = map.hasOwnProperty(key);
        const defaultVal = getDefaultLabel(mapKey, key);
        const displayVal = hasCustom ? map[key] : defaultVal;
        html += `<div class="flex-row" style="gap:6px;">
            <input type="text" value="${key}" placeholder="Колонка" style="width:auto; flex:1;" readonly>
            <input type="text" value="${displayVal}" placeholder="Отображаемое имя" style="width:auto; flex:1;" data-action="updateColLabel" data-map-key="${mapKey}" data-key="${key.replace(/"/g, '&quot;')}">
            ${hasCustom ? `<button data-action="removeColLabel" data-map-key="${mapKey}" data-key="${key.replace(/"/g, '&quot;')}" class="danger" style="padding:2px 8px; font-size:12px;">✕</button>` : ''}
        </div>`;
    });
    html += '</div>';
    container.innerHTML = html;
}

(window as any).updateColLabel = function(mapKey: string, key: string, newVal: string) {
    if (!(state.colLabels as any)[mapKey]) (state.colLabels as any)[mapKey] = {};
    const trimmed = newVal.trim();
    const defaultVal = getDefaultLabel(mapKey, key);
    if (trimmed && trimmed !== defaultVal) {
        (state.colLabels as any)[mapKey][key] = trimmed;
    } else {
        delete (state.colLabels as any)[mapKey][key];
    }
};

(window as any).removeColLabel = function(mapKey: string, key: string) {
    if ((state.colLabels as any)[mapKey]) {
        delete (state.colLabels as any)[mapKey][key];
        renderRenameEditor(mapKey === 'master_parser' ? 'admin-mp-columns' : 'admin-sv-columns', mapKey);
    }
};

(window as any).addCustomColLabel = function(mapKey: string) {
    const key = prompt('Введите системное имя колонки:');
    if (!key) return;
    if (!(state.colLabels as any)[mapKey]) (state.colLabels as any)[mapKey] = {};
    (state.colLabels as any)[mapKey][key] = (state.colLabels as any)[mapKey][key] || '';
    renderRenameEditor(mapKey === 'master_parser' ? 'admin-mp-columns' : 'admin-sv-columns', mapKey);
};

const adminRenameSaveBtn = getButton('admin-rename-save');
if (adminRenameSaveBtn) adminRenameSaveBtn.onclick = saveColLabels;

const adminRenameResetBtn = getButton('admin-rename-reset');
if (adminRenameResetBtn) adminRenameResetBtn.onclick = async () => {
    if (!confirm('Сбросить все переименования?')) return;
    state.colLabels = { master_parser: {}, sveta_server: {} };
    localStorage.removeItem('colLabels');
    setSystemDict({ ...defaultSystemDict });
    await apiCall('/api/admin/column_labels', 'POST', { labels: state.colLabels });
    renderRenameEditor('admin-mp-columns', 'master_parser');
    renderRenameEditor('admin-sv-columns', 'sveta_server');
    showToast('Переименования сброшены', 'success');
};

const adminMpAddBtn = getButton('admin-mp-add');
if (adminMpAddBtn) adminMpAddBtn.onclick = () => (window as any).addCustomColLabel('master_parser');

const adminSvAddBtn = getButton('admin-sv-add');
if (adminSvAddBtn) adminSvAddBtn.onclick = () => (window as any).addCustomColLabel('sveta_server');

export async function loadAdminUsers() {
    const data = await apiCall('/api/admin/users', 'GET');
    const container = getEl<HTMLElement>('admin-users-container');
    if (!container) return;
    if (!data || !data.users) {
        container.innerHTML = '<div class="warning">Не удалось загрузить пользователей</div>';
        return;
    }
    let html = '<table class="ref-table"><thead><tr><th>Логин</th><th>Роль</th><th>Действия</th></tr></thead><tbody>';
    data.users.forEach((u: any) => {
        html += `<tr>
            <td>${u.username}</td>
            <td>
                <select data-action="updateUserRole" data-username="${u.username.replace(/"/g, '&quot;')}" style="width:auto;">
                    <option value="user" ${u.role === 'user' ? 'selected' : ''}>Пользователь</option>
                    <option value="admin" ${u.role === 'admin' ? 'selected' : ''}>Администратор</option>
                </select>
            </td>
            <td>
                <button data-action="resetUserPassword" data-username="${u.username.replace(/"/g, '&quot;')}" title="Сбросить пароль на стандартный">🔑 Сбросить пароль</button>
                <button data-action="deleteUser" data-username="${u.username.replace(/"/g, '&quot;')}" class="danger" ${u.username === currentUser ? 'disabled' : ''}>🗑️ Удалить</button>
            </td>
        </tr>`;
    });
    html += '</tbody></table>';
    container.innerHTML = html;
}

(window as any).updateUserRole = async function(username: string, role: string) {
    const res = await apiCall(`/api/admin/users/${encodeURIComponent(username)}/role`, 'POST', { role });
    if (res && res.success) {
        showToast(`Роль пользователя ${username} обновлена`, 'success');
        await loadAdminUsers();
    }
};

(window as any).deleteUser = async function(username: string) {
    if (!confirm(`Удалить пользователя ${username}?`)) return;
    const res = await apiCall(`/api/admin/users/${encodeURIComponent(username)}/delete`, 'POST', {});
    if (res && res.success) {
        showToast(`Пользователь ${username} удалён`, 'success');
        await loadAdminUsers();
    }
};

(window as any).resetUserPassword = async function(username: string) {
    if (!confirm(`Сбросить пароль пользователя ${username} на стандартный?`)) return;
    const res = await apiCall(`/api/admin/users/${encodeURIComponent(username)}/reset_password`, 'POST', {});
    if (res && res.success) {
        showToast(`Пароль пользователя ${username} сброшен: ${res.default_password}`, 'success');
    }
};

// -------------------- АДМИНКА: УПРАВЛЕНИЕ АТРИБУТАМИ --------------------
export async function loadAdminAttrEditor() {
    const groupSelect = getSelect('admin-attr-group-select');
    if (!groupSelect) return;
    const group = groupSelect.value;
    if (!group) return;
    const data = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, 'GET');
    const container = getEl<HTMLElement>('admin-attr-editor');
    if (!container) return;
    if (!data || !data.attributes) {
        container.innerHTML = '<div class="warning">Не удалось загрузить атрибуты</div>';
        return;
    }
    let html = '<table class="ref-table"><thead><tr><th>Атрибут</th><th>Тип</th><th>Видим в стратегиях</th></tr></thead><tbody>';
    data.attributes.forEach((a: any) => {
        const disabled = a.is_required ? 'disabled title="Обязательный атрибут"' : '';
        html += `<tr>
            <td>${a.label}</td>
            <td>${a.type}</td>
            <td><input type="checkbox" data-action="toggleAttrVisibility" data-group="${group}" data-attr-id="${a.attr_id}" ${a.is_visible ? 'checked' : ''} ${disabled}></td>
        </tr>`;
    });
    html += '</tbody></table>';
    container.innerHTML = html;
}

export async function initExportEditor(): Promise<void> {
    const sourceSelect = getSelect('admin-export-source');
    const groupSelect = getSelect('admin-export-group');
    const sessionWrapper = getEl<HTMLElement>('admin-export-session-wrapper');
    const sessionSelect = getSelect('admin-export-session');
    const formatSelect = getSelect('admin-export-format');
    const btn = getButton('admin-export-btn');
    const columnsToggle = getButton('admin-export-columns-toggle');
    const overridePanel = getEl<HTMLElement>('admin-export-override-panel');
    const overrideList = getEl<HTMLElement>('admin-export-override-list');
    if (!sourceSelect || !groupSelect || !sessionWrapper || !sessionSelect || !formatSelect || !btn) return;

    const groupsRes = await apiCall('/api/reference/groups?all_groups=true', 'GET');
    const groups = (groupsRes?.groups || []) as any[];
    groupSelect.innerHTML =
        '<option value="">-- выберите --</option>' +
        groups.map((g: any) => `<option value="${g.value}">${g.label}</option>`).join('');

    async function loadSessions() {
        if (sourceSelect.value !== 'req') {
            sessionWrapper.style.display = 'none';
            sessionSelect.innerHTML = '';
            return;
        }
        const res = await apiCall('/api/admin/req/sessions', 'GET');
        const sessions = (res?.sessions || []) as any[];
        sessionSelect.innerHTML =
            '<option value="">-- выберите --</option>' +
            sessions.map((s: any) => `<option value="${s.session_id}">${formatFileLabel(s.filename, s.created_at)} (${s.total_rows || 0})</option>`).join('');
        sessionWrapper.style.display = 'block';
    }

    sourceSelect.onchange = () => { loadSessions(); };
    await loadSessions();

    async function renderOverridePanel() {
        if (!overrideList) return;
        const scope = `admin_${sourceSelect.value}`;
        const res = await apiCall(`/api/admin/export_columns?scope=${encodeURIComponent(scope)}`, 'GET');
        const options: any[] = res?.options || [];
        const cfg: any = res?.config || { exclude: [], include: null };
        let included = new Set<string>();
        if (cfg.include && cfg.include.length) {
            included = new Set(cfg.include);
        } else {
            const exclude = new Set(cfg.exclude || []);
            options.forEach((o: any) => { if (!exclude.has(o.key)) included.add(o.key); });
        }
        let html = '';
        options.forEach((opt: any) => {
            const checked = included.has(opt.key) ? 'checked' : '';
            const serviceStyle = opt.service ? 'color:#888; font-style:italic;' : '';
            html += `<label style="display:flex; align-items:center; gap:6px; cursor:pointer; background:#fff; padding:6px 12px; border-radius:20px; border:1px solid #e0e0e0; font-size:13px; ${serviceStyle}">
                <input type="checkbox" value="${opt.key}" data-export-override-col ${checked}> ${opt.label}
            </label>`;
        });
        overrideList.innerHTML = html;
    }

    if (columnsToggle && overridePanel) {
        columnsToggle.onclick = async () => {
            const isHidden = overridePanel.style.display === 'none';
            overridePanel.style.display = isHidden ? 'block' : 'none';
            if (isHidden) await renderOverridePanel();
        };
    }

    btn.onclick = async () => {
        const source = sourceSelect.value;
        const group = groupSelect.value;
        const sessionId = sessionSelect.value;
        const format = formatSelect.value;
        if (!group) {
            showToast('Выберите группу', 'error');
            return;
        }
        if (source === 'req' && !sessionId) {
            showToast('Выберите сессию', 'error');
            return;
        }
        const params = new URLSearchParams({ group, format });
        if (source === 'req') params.set('session_id', sessionId);
        if (overridePanel && overridePanel.style.display !== 'none' && overrideList) {
            const checked = Array.from(overrideList.querySelectorAll<HTMLInputElement>('input[data-export-override-col]:checked')).map(cb => cb.value);
            if (checked.length) params.set('columns', JSON.stringify(checked));
        }
        const url = `/api/admin/export/${source}?${params.toString()}`;
        const token = localStorage.getItem('token');
        try {
            const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(data.error || data.detail || `Ошибка ${resp.status}`);
            }
            const blob = await resp.blob();
            const cd = resp.headers.get('content-disposition') || '';
            let filename = `export_${group}_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}.${format}`;
            const match = cd.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
            if (match) filename = decodeURIComponent(match[1]);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
        } catch (err: any) {
            showToast(err.message, 'error');
        }
    };
}

(window as any).toggleAttrVisibility = async function(group: string, attrId: number, isVisible: boolean) {
    const res = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, 'POST', { attr_id: attrId, is_visible: isVisible });
    if (res && res.success) {
        showToast('Настройка видимости обновлена', 'success');
    }
};

export async function initMlExportEditor(): Promise<void> {
    const sourceSelect = getSelect('admin-ml-export-source');
    const groupSelect = getSelect('admin-ml-export-group');
    const sessionWrapper = getEl<HTMLElement>('admin-ml-export-session-wrapper');
    const sessionSelect = getSelect('admin-ml-export-session');
    const csvBtn = getButton('admin-ml-export-csv');
    const xlsxBtn = getButton('admin-ml-export-xlsx');
    if (!sourceSelect || !groupSelect || !sessionWrapper || !sessionSelect || !csvBtn || !xlsxBtn) return;

    const groupsRes = await apiCall('/api/groups', 'GET');
    const groups = (groupsRes?.groups || []) as any[];
    groupSelect.innerHTML =
        '<option value="">-- выберите --</option>' +
        groups.map((g: any) => `<option value="${g.group_key}">${g.group_name}</option>`).join('');

    async function loadSessions() {
        if (sourceSelect.value !== 'req') {
            sessionWrapper.style.display = 'none';
            sessionSelect.innerHTML = '';
            return;
        }
        const res = await apiCall('/api/admin/req/sessions', 'GET');
        const sessions = (res?.sessions || []) as any[];
        sessionSelect.innerHTML =
            '<option value="">-- выберите --</option>' +
            sessions.map((s: any) => `<option value="${s.session_id}">${formatFileLabel(s.filename, s.created_at)} (${s.total_rows || 0})</option>`).join('');
        sessionWrapper.style.display = 'block';
    }

    sourceSelect.onchange = () => { void loadSessions(); };
    await loadSessions();

    async function doExport(format: string) {
        const source = sourceSelect.value;
        const group = groupSelect.value;
        const sessionId = sessionSelect.value;
        if (!group) {
            showToast('Выберите группу', 'error');
            return;
        }
        if (source === 'req' && !sessionId) {
            showToast('Выберите сессию', 'error');
            return;
        }
        const params = new URLSearchParams({ group, format });
        if (source === 'req') params.set('session_id', sessionId);
        const url = `/api/admin/export_ml_sample/${source}?${params.toString()}`;
        const token = localStorage.getItem('token');
        try {
            const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(data.error || data.detail || `Ошибка ${resp.status}`);
            }
            const blob = await resp.blob();
            const cd = resp.headers.get('content-disposition') || '';
            let filename = `ml_sample_${group}_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}.${format}`;
            const match = cd.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
            if (match) filename = decodeURIComponent(match[1]);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
        } catch (err: any) {
            showToast(err.message, 'error');
        }
    }

    csvBtn.onclick = () => { void doExport('csv'); };
    xlsxBtn.onclick = () => { void doExport('xlsx'); };
}

export function openProfileModal() {
    const profileUsername = getEl<HTMLElement>('profile-username');
    if (profileUsername) profileUsername.innerText = currentUser || '';
    const oldPassword = getInput('profile-old-password');
    if (oldPassword) oldPassword.value = '';
    const newPassword = getInput('profile-new-password');
    if (newPassword) newPassword.value = '';
    const confirmPassword = getInput('profile-confirm-password');
    if (confirmPassword) confirmPassword.value = '';
    const modal = getEl<HTMLElement>('profile-modal');
    if (modal) modal.style.display = 'flex';
    loadUserDisplayColumns();
}

export function closeProfileModal() {
    const modal = getEl<HTMLElement>('profile-modal');
    if (modal) modal.style.display = 'none';
}

export async function doChangePassword() {
    const oldPassword = getInput('profile-old-password')?.value || '';
    const newPassword = getInput('profile-new-password')?.value || '';
    const confirmPassword = getInput('profile-confirm-password')?.value || '';
    if (!oldPassword || !newPassword) {
        showToast('Заполните все поля', 'warning');
        return;
    }
    if (newPassword !== confirmPassword) {
        showToast('Новый пароль и подтверждение не совпадают', 'warning');
        return;
    }
    const res = await apiCall('/api/user/change_password', 'POST', { old_password: oldPassword, new_password: newPassword });
    if (res && res.success) {
        showToast('Пароль успешно изменён', 'success');
        closeProfileModal();
    }
}

const addUserBtn = getButton('add-user-btn');
if (addUserBtn) addUserBtn.onclick = async () => {
    const login = getInput('new-user-login')?.value.trim() || '';
    const role = getSelect('new-user-role')?.value || '';
    if (!login) {
        showToast('Введите логин', 'warning');
        return;
    }
    const res = await apiCall('/api/admin/users', 'POST', { username: login, role });
    if (res && res.success) {
        showToast('Пользователь добавлен', 'success');
        const resultDiv = getEl<HTMLElement>('new-user-result');
        if (resultDiv) {
            resultDiv.innerText = `Пользователь ${login} создан. Пароль: ${res.default_password}`;
            resultDiv.style.display = 'block';
        }
        const loginInput = getInput('new-user-login');
        if (loginInput) loginInput.value = '';
        await loadAdminUsers();
    }
};

// ---------- Search Config Admin ----------
export async function loadSearchConfig() {
    const res = await apiCall('/api/admin/search_config', 'GET');
    const cfg: Record<string, any> = res?.config || {};
    const price: Record<string, any> = cfg.price || {};
    const qty: Record<string, any> = cfg.qty || {};
    const territorial: Record<string, any> = cfg.territorial || {};

    const priceEnabled = getInput('search-config-price-enabled');
    if (priceEnabled) priceEnabled.checked = !!price.enabled;
    const priceSort = document.querySelectorAll<HTMLInputElement>('input[name="search-config-price-sort"]');
    priceSort.forEach(r => r.checked = (r.value === (price.sort_asc !== false ? 'asc' : 'desc')));

    const qtyEnabled = getInput('search-config-qty-enabled');
    if (qtyEnabled) qtyEnabled.checked = !!qty.enabled;
    const qtySort = document.querySelectorAll<HTMLInputElement>('input[name="search-config-qty-sort"]');
    const qtySortValue = qty.sort_desc === true ? 'desc' : (qty.sort_asc === true ? 'asc' : 'desc');
    qtySort.forEach(r => r.checked = (r.value === qtySortValue));

    const shortage = document.querySelectorAll<HTMLInputElement>('input[name="search-config-shortage"]');
    shortage.forEach(r => r.checked = (r.value === (cfg.optimal_shortage_behavior || 'cover_req_qty')));

    const territorialEnabled = getInput('search-config-territorial-enabled');
    if (territorialEnabled) territorialEnabled.checked = !!territorial.enabled;
    const territorialEditor = getEl<HTMLElement>('search-config-territorial-editor');
    if (territorialEditor) territorialEditor.style.display = territorial.enabled ? 'block' : 'none';
}

const searchConfigSaveBtn = getButton('search-config-save');
if (searchConfigSaveBtn) searchConfigSaveBtn.onclick = async () => {
    const currentCfgRes = await apiCall('/api/admin/search_config', 'GET');
    const currentCfg: Record<string, any> = currentCfgRes?.config || {};
    const priceChecked = document.querySelector<HTMLInputElement>('input[name="search-config-price-sort"]:checked');
    const qtyChecked = document.querySelector<HTMLInputElement>('input[name="search-config-qty-sort"]:checked');
    const shortageChecked = document.querySelector<HTMLInputElement>('input[name="search-config-shortage"]:checked');
    const priceEnabled = getInput('search-config-price-enabled');
    const qtyEnabled = getInput('search-config-qty-enabled');
    const territorialEnabled = getInput('search-config-territorial-enabled');
    const config: Record<string, any> = {
        price: {
            enabled: priceEnabled ? priceEnabled.checked : false,
            sort_asc: priceChecked?.value === 'asc',
        },
        qty: {
            enabled: qtyEnabled ? qtyEnabled.checked : false,
            sort_desc: qtyChecked?.value === 'desc',
            sort_asc: qtyChecked?.value === 'asc',
        },
        territorial: {
            enabled: territorialEnabled ? territorialEnabled.checked : false,
            ref_column: 'territory',
            req_column: 'territory',
            matrix: currentCfg.territorial?.matrix || {},
        },
        optimal_shortage_behavior: shortageChecked?.value || 'cover_req_qty',
    };
    const res = await apiCall('/api/admin/search_config', 'POST', { config });
    const resultEl = getEl<HTMLElement>('search-config-result');
    if (resultEl) {
        resultEl.textContent = res?.success ? 'Сохранено' : 'Ошибка';
        resultEl.style.display = 'inline';
        setTimeout(() => resultEl.style.display = 'none', 3000);
    }
};

const searchConfigTerritorialEnabled = getInput('search-config-territorial-enabled');
if (searchConfigTerritorialEnabled) searchConfigTerritorialEnabled.onchange = function() {
    const editor = getEl<HTMLElement>('search-config-territorial-editor');
    if (editor) editor.style.display = (this as HTMLInputElement).checked ? 'block' : 'none';
};

// ---------- Настройка колонок массового поиска ----------
let massColumnOptionsData: any[] = [];
let massColumnsSelected: string[] | null = null;
let massShowAttributes = false;

export async function loadMassColumnsEditor() {
    const res = await apiCall('/api/admin/mass_columns', 'GET');
    massColumnOptionsData = res?.options || [];
    massColumnsSelected = res?.columns || null;
    massShowAttributes = !!res?.show_attributes;
    renderMassColumnsEditor();
}

function renderMassColumnsEditor() {
    const container = getEl<HTMLElement>('admin-mass-columns-editor');
    if (!container) return;
    const toggle = getInput('admin-mass-show-attributes');
    if (toggle) toggle.checked = massShowAttributes;
    let html = '';
    const selectedSet = new Set(massColumnsSelected || []);
    massColumnOptionsData.forEach((opt: any) => {
        const checked = (massColumnsSelected === null || selectedSet.has(opt.key)) ? 'checked' : '';
        html += `<label style="display:flex; align-items:center; gap:6px; cursor:pointer; background:#fff; padding:6px 12px; border-radius:20px; border:1px solid #e0e0e0;">
            <input type="checkbox" value="${opt.key}" ${checked}> ${opt.label}
        </label>`;
    });
    container.innerHTML = html;
}

const adminMassColumnsSaveBtn = getButton('admin-mass-columns-save');
if (adminMassColumnsSaveBtn) adminMassColumnsSaveBtn.onclick = async () => {
    const checkboxes = document.querySelectorAll<HTMLInputElement>('#admin-mass-columns-editor input[type="checkbox"]:checked');
    const selected = Array.from(checkboxes).map(cb => cb.value);
    const showAttrs = getInput('admin-mass-show-attributes')?.checked ?? false;
    const res = await apiCall('/api/admin/mass_columns', 'POST', { columns: selected, show_attributes: showAttrs });
    if (res && res.success) {
        massColumnsSelected = selected;
        massShowAttributes = showAttrs;
        resetMassColumnSettingsCache();
        showToast('Колонки массового поиска сохранены', 'success');
    }
};

const adminMassColumnsResetBtn = getButton('admin-mass-columns-reset');
if (adminMassColumnsResetBtn) adminMassColumnsResetBtn.onclick = async () => {
    const res = await apiCall('/api/admin/mass_columns', 'POST', { columns: null, show_attributes: false });
    if (res && res.success) {
        massColumnsSelected = null;
        massShowAttributes = false;
        resetMassColumnSettingsCache();
        renderMassColumnsEditor();
        showToast('Колонки сброшены (по умолчанию)', 'success');
    }
};

// ---------- Настройка колонок одиночного поиска ----------
let singleColumnOptionsData: any[] = [];
let singleColumnsSelected: string[] | null = null;
let singleShowAttributes = false;

export async function loadSingleColumnsEditor() {
    const res = await apiCall('/api/admin/single_columns', 'GET');
    singleColumnOptionsData = res?.options || [];
    singleColumnsSelected = res?.columns || null;
    singleShowAttributes = !!res?.show_attributes;
    renderSingleColumnsEditor();
}

function renderSingleColumnsEditor() {
    const container = getEl<HTMLElement>('admin-single-columns-editor');
    if (!container) return;
    const toggle = getInput('admin-single-show-attributes');
    if (toggle) toggle.checked = singleShowAttributes;
    let html = '';
    const selectedSet = new Set(singleColumnsSelected || []);
    singleColumnOptionsData.forEach((opt: any) => {
        const checked = (singleColumnsSelected === null || selectedSet.has(opt.key)) ? 'checked' : '';
        html += `<label style="display:flex; align-items:center; gap:6px; cursor:pointer; background:#fff; padding:6px 12px; border-radius:20px; border:1px solid #e0e0e0;">
            <input type="checkbox" value="${opt.key}" ${checked}> ${opt.label}
        </label>`;
    });
    container.innerHTML = html;
}

const adminSingleColumnsSaveBtn = getButton('admin-single-columns-save');
if (adminSingleColumnsSaveBtn) adminSingleColumnsSaveBtn.onclick = async () => {
    const checkboxes = document.querySelectorAll<HTMLInputElement>('#admin-single-columns-editor input[type="checkbox"]:checked');
    const selected = Array.from(checkboxes).map(cb => cb.value);
    const showAttrs = getInput('admin-single-show-attributes')?.checked ?? false;
    const res = await apiCall('/api/admin/single_columns', 'POST', { columns: selected, show_attributes: showAttrs });
    if (res && res.success) {
        singleColumnsSelected = selected;
        singleShowAttributes = showAttrs;
        resetSingleColumnSettingsCache();
        showToast('Колонки одиночного поиска сохранены', 'success');
    }
};

const adminSingleColumnsResetBtn = getButton('admin-single-columns-reset');
if (adminSingleColumnsResetBtn) adminSingleColumnsResetBtn.onclick = async () => {
    const res = await apiCall('/api/admin/single_columns', 'POST', { columns: null, show_attributes: false });
    if (res && res.success) {
        singleColumnsSelected = null;
        singleShowAttributes = false;
        resetSingleColumnSettingsCache();
        renderSingleColumnsEditor();
        showToast('Колонки сброшены (по умолчанию)', 'success');
    }
};

// ---------- Настройка колонок выгрузок ----------
let exportColumnOptions: Record<string, any[]> = {};
let exportColumnConfigs: Record<string, { exclude?: string[]; include?: string[] | null }> = {};
let currentExportScope = 'admin_ref';

export async function initExportColumnsEditor() {
    const scopeSelect = getSelect('admin-export-columns-scope');
    const useIncludeCb = getInput('admin-export-use-include');
    const saveBtn = getButton('admin-export-columns-save');
    const resetBtn = getButton('admin-export-columns-reset');
    const addCustomBtn = getButton('admin-export-add-custom');
    if (!scopeSelect) return;

    scopeSelect.onchange = async () => {
        currentExportScope = scopeSelect.value;
        await loadExportColumnsEditor(currentExportScope);
    };
    if (useIncludeCb) useIncludeCb.onchange = () => renderExportColumnsEditor();
    if (saveBtn) saveBtn.onclick = saveExportColumns;
    if (resetBtn) resetBtn.onclick = resetExportColumns;
    if (addCustomBtn) addCustomBtn.onclick = addCustomExportColumn;

    await loadExportColumnsEditor(currentExportScope);
}

async function loadExportColumnsEditor(scope: string) {
    const res = await apiCall(`/api/admin/export_columns?scope=${encodeURIComponent(scope)}`, 'GET');
    exportColumnOptions[scope] = res?.options || [];
    exportColumnConfigs[scope] = res?.config || { exclude: [], include: null };
    const useIncludeCb = getInput('admin-export-use-include');
    if (useIncludeCb) useIncludeCb.checked = !!exportColumnConfigs[scope].include;
    renderExportColumnsEditor();
}

function renderExportColumnsEditor() {
    const container = getEl<HTMLElement>('admin-export-columns-editor');
    const scopeSelect = getSelect('admin-export-columns-scope');
    if (!container || !scopeSelect) return;
    const scope = scopeSelect.value;
    const options = exportColumnOptions[scope] || [];
    const cfg = exportColumnConfigs[scope] || { exclude: [], include: null };
    const useInclude = getInput('admin-export-use-include')?.checked || false;
    const selected = new Set<string>(useInclude ? (cfg.include || []) : (cfg.exclude || []));
    let html = '';
    options.forEach((opt: any) => {
        const checked = selected.has(opt.key) ? 'checked' : '';
        const serviceStyle = opt.service ? 'color:#888; font-style:italic;' : '';
        html += `<label style="display:flex; align-items:center; gap:6px; cursor:pointer; background:#fff; padding:6px 12px; border-radius:20px; border:1px solid #e0e0e0; font-size:13px; ${serviceStyle}">
            <input type="checkbox" value="${opt.key}" data-export-col ${checked}> ${opt.label}
        </label>`;
    });
    container.innerHTML = html;
}

async function saveExportColumns() {
    const scopeSelect = getSelect('admin-export-columns-scope');
    if (!scopeSelect) return;
    const scope = scopeSelect.value;
    const useInclude = getInput('admin-export-use-include')?.checked || false;
    const checkboxes = document.querySelectorAll<HTMLInputElement>('#admin-export-columns-editor input[data-export-col]:checked');
    const selected = Array.from(checkboxes).map(cb => cb.value);
    const config = useInclude
        ? { include: selected, exclude: [] }
        : { exclude: selected, include: null };
    const res = await apiCall('/api/admin/export_columns', 'POST', { scope, config });
    if (res && res.success) {
        exportColumnConfigs[scope] = config;
        showToast('Настройки выгрузок сохранены', 'success');
    }
}

async function resetExportColumns() {
    const scopeSelect = getSelect('admin-export-columns-scope');
    if (!scopeSelect) return;
    const scope = scopeSelect.value;
    const res = await apiCall('/api/admin/export_columns', 'POST', { scope, config: null });
    if (res && res.success) {
        await loadExportColumnsEditor(scope);
        showToast('Настройки выгрузок сброшены по умолчанию', 'success');
    }
}

function addCustomExportColumn() {
    const input = getInput('admin-export-custom-col');
    const scopeSelect = getSelect('admin-export-columns-scope');
    if (!input || !scopeSelect) return;
    const key = input.value.trim();
    if (!key) return;
    const scope = scopeSelect.value;
    if (!exportColumnOptions[scope]) exportColumnOptions[scope] = [];
    if (!exportColumnOptions[scope].find((o: any) => o.key === key)) {
        exportColumnOptions[scope].push({ key, label: key, service: false });
    }
    const cfg = exportColumnConfigs[scope] || { exclude: [], include: null };
    const useInclude = getInput('admin-export-use-include')?.checked || false;
    if (useInclude) {
        cfg.include = [...new Set([...(cfg.include || []), key])];
    } else {
        cfg.exclude = [...new Set([...(cfg.exclude || []), key])];
    }
    exportColumnConfigs[scope] = cfg;
    input.value = '';
    renderExportColumnsEditor();
}

let territoryMatrixData: { values: string[]; matrix: Record<string, any> } = { values: [], matrix: {} };

async function loadTerritoryMatrix() {
    const valuesRes = await apiCall('/api/admin/territory_values', 'GET');
    const matrixRes = await apiCall('/api/admin/territory_matrix', 'GET');
    const values = valuesRes?.ref_values || [];
    const matrix = matrixRes?.matrix || {};
    territoryMatrixData = { values, matrix };
    renderTerritoryMatrix(values, matrix);
}

function renderTerritoryMatrix(values: string[], matrix: Record<string, any>) {
    const container = getEl<HTMLElement>('territory-matrix-container');
    if (!container) return;
    if (!values.length) {
        container.innerHTML = '<div style="padding:12px; color:#666; font-size:13px;">Нет данных. Загрузите справочники с колонкой territory.</div>';
        return;
    }
    // Build lookup: (refVal, reqVal) -> group
    const groupMap: Record<string, number> = {};
    for (const g of ['group_1', 'group_2', 'group_3', 'group_4']) {
        const pairs = matrix[g] || [];
        for (const p of pairs) {
            if (Array.isArray(p) && p.length >= 2) {
                groupMap[`${p[0]}|${p[1]}`] = parseInt(g.split('_')[1]);
            }
        }
    }
    let html = '<table style="border-collapse:collapse; font-size:12px;"><thead><tr><th style="padding:6px; border:1px solid #ddd; background:#f5f5f5; position:sticky; left:0; z-index:1;">Ref \\ Ref</th>';
    values.forEach(v => {
        html += `<th style="padding:6px; border:1px solid #ddd; background:#f5f5f5; min-width:60px; text-align:center;" title="${v}">${v.length > 8 ? v.slice(0,8)+'…' : v}</th>`;
    });
    html += '</tr></thead><tbody>';
    values.forEach(rowVal => {
        html += `<tr><td style="padding:6px; border:1px solid #ddd; font-weight:600; position:sticky; left:0; background:#fff; white-space:nowrap;" title="${rowVal}">${rowVal.length > 12 ? rowVal.slice(0,12)+'…' : rowVal}</td>`;
        values.forEach(colVal => {
            const key = `${rowVal}|${colVal}`;
            const group = groupMap[key] || (rowVal === colVal ? 1 : 4);
            const selectId = `tm-${encodeURIComponent(rowVal)}-${encodeURIComponent(colVal)}`;
            const bg = rowVal === colVal ? '#e8f5e9' : '#fff';
            html += `<td style="padding:3px; border:1px solid #ddd; text-align:center; background:${bg};">
                <select id="${selectId}" data-row="${rowVal}" data-col="${colVal}" style="font-size:11px; padding:1px; width:44px; border:none; background:transparent; cursor:pointer;">
                    <option value="1" ${group === 1 ? 'selected' : ''}>1</option>
                    <option value="2" ${group === 2 ? 'selected' : ''}>2</option>
                    <option value="3" ${group === 3 ? 'selected' : ''}>3</option>
                    <option value="4" ${group === 4 ? 'selected' : ''}>4</option>
                </select>
            </td>`;
        });
        html += '</tr>';
    });
    html += '</tbody></table>';
    container.innerHTML = html;
}

function getTerritoryMatrixFromUI() {
    const matrix: Record<string, any> = { group_1: [], group_2: [], group_3: [], group_4: [] };
    const selects = document.querySelectorAll<HTMLSelectElement>('#territory-matrix-container select');
    selects.forEach(sel => {
        const rowVal = sel.dataset.row;
        const colVal = sel.dataset.col;
        const group = sel.value;
        matrix[`group_${group}`].push([rowVal, colVal]);
    });
    return matrix;
}

const territoryScanBtn = getButton('territory-scan-btn');
if (territoryScanBtn) territoryScanBtn.onclick = loadTerritoryMatrix;

const territoryAutoBtn = getButton('territory-auto-btn');
if (territoryAutoBtn) territoryAutoBtn.onclick = () => {
    const { values } = territoryMatrixData;
    const matrix: Record<string, any> = { group_1: [], group_2: [], group_3: [], group_4: [] };
    values.forEach(rowVal => {
        values.forEach(colVal => {
            if (rowVal === colVal) {
                matrix.group_1.push([rowVal, colVal]);
            } else {
                matrix.group_4.push([rowVal, colVal]);
            }
        });
    });
    territoryMatrixData.matrix = matrix;
    renderTerritoryMatrix(values, matrix);
};

const territorySaveMatrixBtn = getButton('territory-save-matrix-btn');
if (territorySaveMatrixBtn) territorySaveMatrixBtn.onclick = async () => {
    const matrix = getTerritoryMatrixFromUI();
    const territorialEnabled = getInput('search-config-territorial-enabled');
    const res = await apiCall('/api/admin/territory_matrix', 'POST', {
        config: {
            matrix,
            enabled: territorialEnabled ? territorialEnabled.checked : false,
            ref_column: 'territory',
            req_column: 'territory',
        }
    });
    const resultEl = getEl<HTMLElement>('territory-matrix-result');
    if (resultEl) {
        resultEl.textContent = res?.success ? 'Матрица сохранена' : 'Ошибка сохранения';
        resultEl.style.display = 'block';
        setTimeout(() => resultEl.style.display = 'none', 3000);
    }
};

const randomGenBtn = getButton('random-gen-btn');
if (randomGenBtn) randomGenBtn.onclick = async () => {
    const fields: string[] = [];
    if (getInput('random-gen-qty')?.checked) fields.push('qty');
    if (getInput('random-gen-price')?.checked) fields.push('price');
    const seed = parseInt(getInput('random-gen-seed')?.value || '42') || 42;
    const activeRefs = await apiCall('/api/active_refs', 'GET');
    const fileId = activeRefs?.[0]?.file_id;
    if (!fileId) {
        const resultEl = getEl<HTMLElement>('random-gen-result');
        if (resultEl) resultEl.textContent = 'Нет активных справочников';
        return;
    }
    const res = await apiCall('/api/admin/generate_random_data', 'POST', { file_id: fileId, fields, seed });
    const resultEl = getEl<HTMLElement>('random-gen-result');
    if (resultEl) resultEl.textContent = `Обновлено: ${res?.updated || 0}, пропущено: ${res?.skipped || 0}`;
};

const profileBtn = getButton('profile-btn');
if (profileBtn) profileBtn.onclick = openProfileModal;

// Dropdown toggle
const profileDropbtn = getButton('profile-dropbtn');
if (profileDropbtn) profileDropbtn.onclick = function(e) {
    e.stopPropagation();
    const dropdown = getEl<HTMLElement>('profile-dropdown');
    if (dropdown) dropdown.classList.toggle('show');
};

// Close dropdown when clicking outside
window.addEventListener('click', function() {
    const dropdown = getEl<HTMLElement>('profile-dropdown');
    if (dropdown) dropdown.classList.remove('show');
});

// ---------- Normalized Title Template Editor ----------
let titleTemplateGroups: any[] = [];
let titleTemplateAttrs: any[] = [];
let titleTemplateSelected: string[] = [];
let titleTemplateCurrentGroupId: number | null = null;

export async function initTitleTemplateEditor() {
    const groupSelect = getSelect('admin-title-group-select');
    if (!groupSelect || groupSelect.dataset.initialized) return;

    const res = await apiCall('/api/groups', 'GET');
    titleTemplateGroups = res?.groups || [];
    groupSelect.innerHTML = '<option value="">-- Выберите группу --</option>';
    for (const g of titleTemplateGroups) {
        const opt = document.createElement('option');
        opt.value = g.group_id;
        opt.textContent = g.group_name;
        opt.dataset.key = g.group_key;
        groupSelect.appendChild(opt);
    }

    groupSelect.onchange = onTitleGroupChange;

    const attrSep = getSelect('admin-title-attr-sep');
    if (attrSep) attrSep.onchange = function() {
        const custom = getInput('admin-title-attr-sep-custom');
        if (custom) custom.style.display = (this as HTMLSelectElement).value === 'custom' ? 'block' : 'none';
    };
    const labelSep = getSelect('admin-title-label-sep');
    if (labelSep) labelSep.onchange = function() {
        const custom = getInput('admin-title-label-sep-custom');
        if (custom) custom.style.display = (this as HTMLSelectElement).value === 'custom' ? 'block' : 'none';
    };
    const showLabels = getSelect('admin-title-show-labels');
    if (showLabels) showLabels.onchange = function() {
        const wrapper = getEl<HTMLElement>('admin-title-label-sep-wrapper');
        if (wrapper) wrapper.style.display = (this as HTMLSelectElement).value === 'true' ? 'block' : 'none';
    };

    const saveBtn = getButton('admin-title-save');
    if (saveBtn) saveBtn.onclick = saveTitleTemplate;
    const deleteBtn = getButton('admin-title-delete');
    if (deleteBtn) deleteBtn.onclick = deleteTitleTemplate;
    const previewBtn = getButton('admin-title-preview-btn');
    if (previewBtn) previewBtn.onclick = updateTitlePreview;

    groupSelect.dataset.initialized = 'true';
}

async function onTitleGroupChange() {
    const groupSelect = getSelect('admin-title-group-select');
    if (!groupSelect) return;
    const groupId = parseInt(groupSelect.value);
    const selectedOption = groupSelect.options[groupSelect.selectedIndex] as HTMLOptionElement | undefined;
    const groupKey = selectedOption?.dataset?.key || '';
    titleTemplateCurrentGroupId = groupId;

    const editor = getEl<HTMLElement>('admin-title-template-editor');
    if (!editor) return;
    if (!groupId) {
        editor.style.display = 'none';
        return;
    }
    editor.style.display = 'block';

    const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(groupKey)}`, 'GET');
    titleTemplateAttrs = attrRes?.attributes || [];

    const tmplRes = await apiCall(`/api/admin/title_template/${groupId}?template_name=default`, 'GET');
    const tmpl: Record<string, any> = tmplRes?.template;

    if (tmpl && tmpl.attr_sequence && tmpl.attr_sequence.length > 0) {
        titleTemplateSelected = [...tmpl.attr_sequence];

        const attrSep = tmpl.attr_separator || ', ';
        const attrSepSelect = getSelect('admin-title-attr-sep');
        const attrSepCustom = getInput('admin-title-attr-sep-custom');
        let found = false;
        if (attrSepSelect) {
            for (let i = 0; i < attrSepSelect.options.length; i++) {
                if (attrSepSelect.options[i].value === attrSep) {
                    attrSepSelect.selectedIndex = i;
                    found = true;
                    break;
                }
            }
            if (!found) {
                attrSepSelect.value = 'custom';
                if (attrSepCustom) {
                    attrSepCustom.value = attrSep;
                    attrSepCustom.style.display = 'block';
                }
            } else {
                if (attrSepCustom) attrSepCustom.style.display = 'none';
            }
        }

        const showLabels = tmpl.show_labels !== undefined ? tmpl.show_labels : true;
        const showLabelsSelect = getSelect('admin-title-show-labels');
        if (showLabelsSelect) showLabelsSelect.value = showLabels ? 'true' : 'false';
        const labelSepWrapper = getEl<HTMLElement>('admin-title-label-sep-wrapper');
        if (labelSepWrapper) labelSepWrapper.style.display = showLabels ? 'block' : 'none';

        const labelSep = tmpl.label_separator || ': ';
        const labelSepSelect = getSelect('admin-title-label-sep');
        const labelSepCustom = getInput('admin-title-label-sep-custom');
        found = false;
        if (labelSepSelect) {
            for (let i = 0; i < labelSepSelect.options.length; i++) {
                if (labelSepSelect.options[i].value === labelSep) {
                    labelSepSelect.selectedIndex = i;
                    found = true;
                    break;
                }
            }
            if (!found) {
                labelSepSelect.value = 'custom';
                if (labelSepCustom) {
                    labelSepCustom.value = labelSep;
                    labelSepCustom.style.display = 'block';
                }
            } else {
                if (labelSepCustom) labelSepCustom.style.display = 'none';
            }
        }

        const includeUnit = getInput('admin-title-include-unit');
        if (includeUnit) includeUnit.checked = tmpl.include_unit !== false;
        const deleteBtn = getButton('admin-title-delete');
        if (deleteBtn) deleteBtn.style.display = 'inline-block';
    } else {
        titleTemplateSelected = [];
        const attrSepSelect = getSelect('admin-title-attr-sep');
        if (attrSepSelect) attrSepSelect.selectedIndex = 0;
        const attrSepCustom = getInput('admin-title-attr-sep-custom');
        if (attrSepCustom) attrSepCustom.style.display = 'none';
        const showLabelsSelect = getSelect('admin-title-show-labels');
        if (showLabelsSelect) showLabelsSelect.value = 'true';
        const labelSepWrapper = getEl<HTMLElement>('admin-title-label-sep-wrapper');
        if (labelSepWrapper) labelSepWrapper.style.display = 'block';
        const labelSepSelect = getSelect('admin-title-label-sep');
        if (labelSepSelect) labelSepSelect.selectedIndex = 0;
        const labelSepCustom = getInput('admin-title-label-sep-custom');
        if (labelSepCustom) labelSepCustom.style.display = 'none';
        const includeUnit = getInput('admin-title-include-unit');
        if (includeUnit) includeUnit.checked = true;
        const deleteBtn = getButton('admin-title-delete');
        if (deleteBtn) deleteBtn.style.display = 'none';
    }

    renderTitleTemplateUI();
}

function renderTitleTemplateUI() {
    const availableContainer = getEl<HTMLElement>('admin-title-available-attrs');
    const selectedContainer = getEl<HTMLElement>('admin-title-selected-attrs');
    if (!availableContainer || !selectedContainer) return;

    const special = [
        {key: 'class', label: 'Класс (class)'},
        {key: 'subclass', label: 'Подкласс (subclass)'}
    ];
    const allAvailable = [...special, ...titleTemplateAttrs];
    const selectedSet = new Set(titleTemplateSelected);

    let html = '';
    for (const attr of allAvailable) {
        if (!selectedSet.has(attr.key)) {
            html += `<div style="padding:4px 6px; margin:2px 0; background:#f0f0f0; border-radius:4px; cursor:pointer; display:flex; justify-content:space-between; align-items:center;" data-action="addTitleAttr" data-key="${attr.key.replace(/"/g, '&quot;')}">
                <span>${attr.label}</span>
                <span style="color:#999; font-size:12px;">+</span>
            </div>`;
        }
    }
    availableContainer.innerHTML = html || '<div style="color:#999; padding:8px;">Все атрибуты выбраны</div>';

    html = '';
    for (let i = 0; i < titleTemplateSelected.length; i++) {
        const key = titleTemplateSelected[i];
        let label = key;
        const found = allAvailable.find((a: any) => a.key === key);
        if (found) label = found.label;

        html += `<div style="padding:4px 6px; margin:2px 0; background:#e8f4e8; border-radius:4px; display:flex; justify-content:space-between; align-items:center;">
            <span>${label}</span>
            <span>
                <button data-action="moveTitleAttr" data-key="${key.replace(/"/g, '&quot;')}" data-dir="-1" ${i === 0 ? 'disabled' : ''} style="font-size:11px; padding:1px 4px;">↑</button>
                <button data-action="moveTitleAttr" data-key="${key.replace(/"/g, '&quot;')}" data-dir="1" ${i === titleTemplateSelected.length - 1 ? 'disabled' : ''} style="font-size:11px; padding:1px 4px;">↓</button>
                <button data-action="removeTitleAttr" data-key="${key.replace(/"/g, '&quot;')}" style="font-size:11px; padding:1px 4px; color:#c00;">×</button>
            </span>
        </div>`;
    }
    selectedContainer.innerHTML = html || '<div style="color:#999; padding:8px;">Нет выбранных атрибутов</div>';
}

(window as any).addTitleAttr = function(key: string) {
    if (!titleTemplateSelected.includes(key)) {
        titleTemplateSelected.push(key);
        renderTitleTemplateUI();
    }
};

(window as any).removeTitleAttr = function(key: string) {
    const idx = titleTemplateSelected.indexOf(key);
    if (idx >= 0) {
        titleTemplateSelected.splice(idx, 1);
        renderTitleTemplateUI();
    }
};

(window as any).moveTitleAttr = function(key: string, dir: number) {
    const idx = titleTemplateSelected.indexOf(key);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= titleTemplateSelected.length) return;
    [titleTemplateSelected[idx], titleTemplateSelected[newIdx]] = [titleTemplateSelected[newIdx], titleTemplateSelected[idx]];
    renderTitleTemplateUI();
};

async function saveTitleTemplate() {
    if (!titleTemplateCurrentGroupId) return;
    const attrSepSelect = getSelect('admin-title-attr-sep');
    const attrSepCustom = getInput('admin-title-attr-sep-custom');
    const attrSeparator = attrSepSelect && attrSepSelect.value === 'custom' && attrSepCustom ? attrSepCustom.value : (attrSepSelect ? attrSepSelect.value : ', ');

    const showLabelsSelect = getSelect('admin-title-show-labels');
    const showLabels = showLabelsSelect ? showLabelsSelect.value === 'true' : true;
    const labelSepSelect = getSelect('admin-title-label-sep');
    const labelSepCustom = getInput('admin-title-label-sep-custom');
    const labelSeparator = labelSepSelect && labelSepSelect.value === 'custom' && labelSepCustom ? labelSepCustom.value : (labelSepSelect ? labelSepSelect.value : ': ');

    const includeUnitInput = getInput('admin-title-include-unit');
    const includeUnit = includeUnitInput ? includeUnitInput.checked : true;

    const payload: Record<string, any> = {
        template_name: 'default',
        attr_sequence: titleTemplateSelected,
        attr_separator: attrSeparator,
        show_labels: showLabels,
        label_separator: labelSeparator,
        include_unit: includeUnit,
        is_active: true,
    };

    const res = await apiCall(`/api/admin/title_template/${titleTemplateCurrentGroupId}`, 'POST', payload);
    if (res && res.success) {
        showToast('Шаблон сохранён', 'success');
        const deleteBtn = getButton('admin-title-delete');
        if (deleteBtn) deleteBtn.style.display = 'inline-block';
    } else {
        showToast('Ошибка сохранения шаблона', 'error');
    }
}

async function deleteTitleTemplate() {
    if (!titleTemplateCurrentGroupId) return;
    if (!confirm('Удалить шаблон для этой группы?')) return;
    const res = await apiCall(`/api/admin/title_template/${titleTemplateCurrentGroupId}/default`, 'DELETE');
    if (res && res.success) {
        showToast('Шаблон удалён', 'success');
        titleTemplateSelected = [];
        renderTitleTemplateUI();
        const deleteBtn = getButton('admin-title-delete');
        if (deleteBtn) deleteBtn.style.display = 'none';
    } else {
        showToast('Ошибка удаления шаблона', 'error');
    }
}

async function updateTitlePreview() {
    const previewRecord = getInput('admin-title-preview-record');
    if (!previewRecord) return;
    const recordId = parseInt(previewRecord.value);
    if (!recordId || isNaN(recordId)) {
        showToast('Введите ID записи', 'warning');
        return;
    }
    const attrSepSelect = getSelect('admin-title-attr-sep');
    const attrSepCustom = getInput('admin-title-attr-sep-custom');
    const attrSeparator = attrSepSelect && attrSepSelect.value === 'custom' && attrSepCustom ? attrSepCustom.value : (attrSepSelect ? attrSepSelect.value : ', ');

    const showLabelsSelect = getSelect('admin-title-show-labels');
    const showLabels = showLabelsSelect ? showLabelsSelect.value === 'true' : true;
    const labelSepSelect = getSelect('admin-title-label-sep');
    const labelSepCustom = getInput('admin-title-label-sep-custom');
    const labelSeparator = labelSepSelect && labelSepSelect.value === 'custom' && labelSepCustom ? labelSepCustom.value : (labelSepSelect ? labelSepSelect.value : ': ');

    const includeUnitInput = getInput('admin-title-include-unit');
    const includeUnit = includeUnitInput ? includeUnitInput.checked : true;

    const template: Record<string, any> = {
        attr_sequence: titleTemplateSelected,
        attr_separator: attrSeparator,
        show_labels: showLabels,
        label_separator: labelSeparator,
        include_unit: includeUnit,
    };

    const res = await apiCall('/api/admin/title_preview', 'POST', { record_id: recordId, template });
    const resultEl = getEl<HTMLElement>('admin-title-preview-result');
    if (resultEl) {
        if (res && res.normalized_title !== undefined) {
            resultEl.textContent = res.normalized_title || '(пусто)';
        } else {
            resultEl.textContent = 'Ошибка получения preview';
        }
    }
}

// ---------- Reference record editing helpers ----------
function renderAttrField(attr: any, val: any) {
    const v = (val !== undefined && val !== null) ? val : '';
    let html = `<label>${attr.label}</label>`;
    if (Array.isArray(v)) {
        html += `<textarea id="edit-attr-${attr.key}" rows="2" style="width:100%;">${v.join('\n')}</textarea>`;
    } else if (attr.type === 'boolean') {
        const checkedYes = v === true || v === 'да' ? 'selected' : '';
        const checkedNo = v === false || v === 'нет' ? 'selected' : '';
        html += `<select id="edit-attr-${attr.key}"><option value=""></option><option value="true" ${checkedYes}>да</option><option value="false" ${checkedNo}>нет</option></select>`;
    } else if (attr.type === 'numeric') {
        html += `<input type="number" id="edit-attr-${attr.key}" value="${v}">`;
    } else {
        html += `<input type="text" id="edit-attr-${attr.key}" value="${v}">`;
    }
    return html;
}

function renderClassificationDisplay(value: Partial<ClassificationPickerValue>): string {
    const parts = [value.group_name, value.class_name, value.subclass_name].filter(Boolean);
    return parts.length ? parts.join(' / ') : '-- не выбрано --';
}

async function resolveClassificationValueByName(
    groupName?: string,
    className?: string,
    subclassName?: string
): Promise<Partial<ClassificationPickerValue>> {
    const value: Partial<ClassificationPickerValue> = {};
    if (!groupName) return value;
    const groupsRes = await apiCall('/api/groups', 'GET');
    const group = (groupsRes?.groups || []).find((g: any) => g.group_name === groupName);
    if (!group) return value;
    value.group_id = group.group_id;
    value.group_name = group.group_name;
    if (!className) return value;
    const classesRes = await apiCall(`/api/admin/taxonomy/classes?group_id=${group.group_id}`, 'GET');
    const cls = (classesRes?.classes || []).find((c: any) => c.class_name === className);
    if (!cls) return value;
    value.class_id = cls.class_id;
    value.class_name = cls.class_name;
    if (!subclassName) return value;
    const subsRes = await apiCall(`/api/admin/taxonomy/subclasses?class_id=${cls.class_id}`, 'GET');
    const sub = (subsRes?.subclasses || []).find((s: any) => s.subclass_name === subclassName);
    if (!sub) return value;
    value.subclass_id = sub.subclass_id;
    value.subclass_name = sub.subclass_name;
    return value;
}

async function buildRecordEditForm(parsed: Record<string, any>, isEdit: boolean) {
    const group = state.currentRefGroup;
    if (!group) return '';
    const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, 'GET');
    const allAttrs: any[] = attrRes.attributes || [];
    const defaultCount = 5;
    const defaultAttrs = allAttrs.slice(0, defaultCount);
    const extraAttrs = allAttrs.slice(defaultCount);
    state.editAttrTypes = {};
    for (const a of allAttrs) state.editAttrTypes[a.key] = a.type;
    state.editExtraAttrs = extraAttrs;
    state.editParsed = parsed;
    const currentGroupName = parsed['group_name'] || group;
    const currentClass = parsed['class'] || parsed['class_name'] || '';
    const currentSubclass = parsed['subclass'] || parsed['subclass_name'] || '';
    const initialValue = { group_name: currentGroupName, class_name: currentClass, subclass_name: currentSubclass };
    let html = '';
    html += `<label>Классификация:</label>`;
    html += `<button type="button" id="edit-classification-btn" data-action="openEditClassificationPicker">Изменить</button>`;
    html += `<input type="hidden" id="edit-classification-value" value='${JSON.stringify(initialValue).replace(/'/g, '&#39;')}'>`;
    html += `<div id="edit-classification-display" style="font-size:13px; color:#555; margin-top:4px;">${renderClassificationDisplay(initialValue)}</div>`;
    html += `<label>Наименование (title_full):</label><input type="text" id="edit-title" value="${parsed['title_full'] || ''}">`;
    for (const attr of defaultAttrs) {
        html += renderAttrField(attr, parsed[attr.key]);
    }
    if (extraAttrs.length > 0) {
        html += `<div style="margin-top:12px;"><label>Добавить атрибут:</label>
            <select id="edit-extra-attr-select">
                <option value="">-- выберите --</option>
                ${extraAttrs.map((a: any) => `<option value="${a.key}">${a.label}</option>`).join('')}
            </select>
            <button type="button" data-action="addExtraEditAttr">+ Добавить</button>
        </div>
        <div id="edit-extra-attrs" style="margin-top:8px; display:flex; flex-direction:column; gap:8px;"></div>`;
    }
    return html;
}

(window as any).addExtraEditAttr = function() {
    const select = getSelect('edit-extra-attr-select');
    if (!select) return;
    const key = select.value;
    if (!key) return;
    const attr = state.editExtraAttrs.find((a: any) => a.key === key);
    if (!attr) return;
    const container = getEl<HTMLElement>('edit-extra-attrs');
    if (!container) return;
    if (getEl<HTMLElement>(`edit-attr-${attr.key}`)) return; // already added
    const wrapper = document.createElement('div');
    wrapper.innerHTML = renderAttrField(attr, state.editParsed[attr.key]);
    container.appendChild(wrapper);
    const option = select.querySelector<HTMLOptionElement>(`option[value="${key}"]`);
    if (option) option.remove();
    select.value = '';
};

(window as any).onEditClassChange = async function(className: string) {
    const select = getSelect('edit-subclass');
    if (!select) return;
    select.innerHTML = '<option value="">-- не выбран --</option>';
    if (!className) return;
    const subRes = await apiCall(`/api/reference/subclasses?class_name=${encodeURIComponent(className)}`, 'GET');
    const subs: string[] = subRes.subclasses || [];
    select.innerHTML += subs.map(s => `<option value="${s}">${s}</option>`).join('');
};

(window as any).openEditClassificationPicker = async function() {
    const input = getInput('edit-classification-value');
    const display = getEl<HTMLElement>('edit-classification-display');
    const current = JSON.parse(input?.value || '{}');
    const initial = await resolveClassificationValueByName(current.group_name, current.class_name, current.subclass_name);
    classificationPicker.open({
        value: initial,
        onSave: (value: ClassificationPickerValue) => {
            const names = { group_name: value.group_name, class_name: value.class_name, subclass_name: value.subclass_name };
            if (input) input.value = JSON.stringify(names);
            if (display) display.textContent = renderClassificationDisplay(names);
        },
    });
};

export async function openRefRecordEditModalSingle(rowData: any) {
    const recordId = rowData.record_id;
    if (!recordId) {
        showToast('У записи отсутствует record_id', 'error');
        return;
    }
    const record = await apiCall(`/api/reference/records/${recordId}`, 'GET');
    if (!record) return;
    state.editingRecordId = recordId;
    state.bulkEditRecordIds = null;
    const titleEl = getEl<HTMLElement>('ref-record-edit-title');
    if (titleEl) titleEl.innerText = 'Редактирование записи';
    const parsed = { ...(record.parsed || {}), title_full: record.title_full || record.parsed?.title_full || '' };
    const form = getEl<HTMLElement>('ref-record-edit-form');
    if (form) form.innerHTML = await buildRecordEditForm(parsed, true) || '';
    const modal = getEl<HTMLElement>('ref-record-edit-modal');
    if (modal) modal.style.display = 'flex';
}

export async function openRefRecordEditModal(isBulk: boolean) {
    const form = getEl<HTMLElement>('ref-record-edit-form');
    if (!form) return;
    if (isBulk) {
        const table = state.tableEditor?.table;
        const selected = table ? table.getSelectedData() : [];
        if (!selected.length) return;
        state.bulkEditRecordIds = selected.map((r: any) => r.record_id).filter(Boolean);
        state.editingRecordId = null;
        const titleEl = getEl<HTMLElement>('ref-record-edit-title');
        if (titleEl) titleEl.innerText = `Редактирование ${selected.length} записей`;
        const group = state.currentRefGroup;
        if (!group) return;
        const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, 'GET');
        const attrs: any[] = attrRes.attributes || [];
        const options = attrs.map(a => `<option value="${a.key}">${a.label}</option>`).join('');
        form.innerHTML = `
            <label>Атрибут:</label>
            <select id="bulk-edit-attr">${options}</select>
            <label>Новое значение:</label>
            <input type="text" id="bulk-edit-value" placeholder="Новое значение">
        `;
    } else {
        state.bulkEditRecordIds = null;
        state.editingRecordId = null;
        const titleEl = getEl<HTMLElement>('ref-record-edit-title');
        if (titleEl) titleEl.innerText = 'Добавление записи';
        form.innerHTML = await buildRecordEditForm({}, false) || '';
    }
    const modal = getEl<HTMLElement>('ref-record-edit-modal');
    if (modal) modal.style.display = 'flex';
}

const refRecordEditCancelBtn = getButton('ref-record-edit-cancel');
if (refRecordEditCancelBtn) refRecordEditCancelBtn.onclick = () => {
    const modal = getEl<HTMLElement>('ref-record-edit-modal');
    if (modal) modal.style.display = 'none';
};

const refRecordEditSaveBtn = getButton('ref-record-edit-save');
if (refRecordEditSaveBtn) refRecordEditSaveBtn.onclick = async () => {
    const modal = getEl<HTMLElement>('ref-record-edit-modal');
    let recordIdsToReprocess: number[] = [];
    if (state.editingRecordId) {
        const changes: Record<string, any> = {};
        const classificationInput = getInput('edit-classification-value');
        const classification = JSON.parse(classificationInput?.value || '{}');
        const titleVal = getInput('edit-title')?.value || '';
        if (classification.group_name) changes.group = classification.group_name;
        if (classification.class_name) changes.class = classification.class_name;
        if (classification.subclass_name) changes.subclass = classification.subclass_name;
        if (titleVal) changes.title_full = titleVal;
        const group = state.currentRefGroup;
        if (!group) return;
        const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, 'GET');
        const attrs: any[] = attrRes.attributes || [];
        document.querySelectorAll<HTMLElement>('[id^="edit-attr-"]').forEach(el => {
            const key = el.id.replace('edit-attr-', '');
            const attrType = state.editAttrTypes ? state.editAttrTypes[key] : null;
            let v: any = (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
            if (el.tagName === 'TEXTAREA') {
                const lines = v.split('\n').map((s: string) => s.trim()).filter((s: string) => s !== '');
                if (lines.length === 0) v = null;
                else if (lines.length === 1) v = lines[0];
                else v = lines;
            } else if (attrType === 'boolean') {
                if (v === 'true') v = true;
                else if (v === 'false') v = false;
                else v = null;
            } else if (attrType === 'numeric') {
                v = v !== '' ? parseFloat(v) : null;
            }
            if (v !== null && v !== '') {
                changes[key] = v;
            }
        });
        await apiCall('/api/table_editor/ref/records/batch_update', 'POST', { updates: [{ record_id: state.editingRecordId, changes }] });
        showToast('Запись обновлена', 'success');
        recordIdsToReprocess = [state.editingRecordId];
    } else if (state.bulkEditRecordIds) {
        const attrName = getSelect('bulk-edit-attr')?.value || '';
        const newValue = getInput('bulk-edit-value')?.value || '';
        if (!attrName || newValue === '') {
            showToast('Укажите атрибут и значение', 'error');
            return;
        }
        const updates = state.bulkEditRecordIds.map(rid => ({ record_id: rid, changes: { [attrName]: newValue } }));
        await apiCall('/api/table_editor/ref/records/batch_update', 'POST', { updates });
        showToast(`Обновлено ${updates.length} записей`, 'success');
        recordIdsToReprocess = state.bulkEditRecordIds;
    } else {
        const classificationInput = getInput('edit-classification-value');
        const classification = JSON.parse(classificationInput?.value || '{}');
        const titleVal = getInput('edit-title')?.value || '';
        if (!titleVal) {
            showToast('Укажите наименование', 'error');
            return;
        }
        const group = state.currentRefGroup;
        if (!group) return;
        const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, 'GET');
        const attrs: any[] = attrRes.attributes || [];
        const attributes: Record<string, any> = {};
        document.querySelectorAll<HTMLElement>('[id^="edit-attr-"]').forEach(el => {
            const key = el.id.replace('edit-attr-', '');
            const attrType = state.editAttrTypes ? state.editAttrTypes[key] : null;
            let v: any = (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
            if (el.tagName === 'TEXTAREA') {
                const lines = v.split('\n').map((s: string) => s.trim()).filter((s: string) => s !== '');
                if (lines.length === 0) v = null;
                else if (lines.length === 1) v = lines[0];
                else v = lines;
            } else if (attrType === 'boolean') {
                if (v === 'true') v = true;
                else if (v === 'false') v = false;
                else v = null;
            } else if (attrType === 'numeric') {
                v = v !== '' ? parseFloat(v) : null;
            }
            if (v !== null && v !== '') {
                attributes[key] = v;
            }
        });
        const createRes = await apiCall('/api/reference/records/create', 'POST', {
            group,
            class: classification.class_name || null,
            subclass: classification.subclass_name || null,
            title_full: titleVal,
            attributes,
        });
        showToast('Запись создана', 'success');
        if (createRes && createRes.record_id) {
            recordIdsToReprocess = [createRes.record_id];
        }
    }
    if (recordIdsToReprocess.length && state.tableEditor) {
        await state.tableEditor.reprocess(recordIdsToReprocess);
    }
    if (modal) modal.style.display = 'none';
    state.tableEditor?.replaceData();
};

// Stubs for user display columns (not implemented yet)
function loadUserDisplayColumns() {
    const container = getEl<HTMLElement>('user-display-columns-list');
    if (container) container.innerHTML = 'Настройки отображения недоступны';
}

function saveUserDisplayColumns() {
    showToast('Настройки отображения недоступны', 'info');
}

(window as any).loadUserDisplayColumns = loadUserDisplayColumns;
(window as any).saveUserDisplayColumns = saveUserDisplayColumns;
(window as any).closeProfileModal = closeProfileModal;
(window as any).doChangePassword = doChangePassword;
